// ============================================================================
// OINP assessment results.
// Publishes a score ONLY when every factor the user answered has a verified
// point value. Otherwise it shows the eligibility verdict plus the factors it
// can score, and says plainly why there is no number — a partial sum would
// read as a real EOI score.
// ============================================================================

import React, { useState } from 'react';
import {
  Wrapper, Eyebrow, StepTitle, StepSubtitle, Card, CardHeading, Row, RowValue,
  Note, GateList, Gauge, PrimaryBtn, GhostBtn, Disclaimer,
  TextInput, InlineRow, NextBtn, ErrorText,
} from '../assessments/oinp/ui';
import { translator } from '../assessments/oinp/i18n';
import { DEADLINES, MAX_SCORE } from '../assessments/oinp/rules';
import { printScorecard } from '../assessments/oinp/print';

const VERDICT_COPY = {
  'appears-eligible': { key: 'verdictEligible', color: '#16a34a' },
  'needs-verification': { key: 'verdictNeedsVerification', color: '#d97706' },
  'not-eligible': { key: 'verdictNotEligible', color: '#dc3545' },
  'needs-job-offer': { key: 'verdictNeedsJobOffer', color: '#d97706' },
};

// Short labels for the failed-requirement list. Mirrors GATE_LABEL_KEYS in ui.jsx.
const GATE_LABELS = {
  'work-experience': 'gateExperience',
  'language-minimum': 'gateLanguage',
  'post-secondary-credential': 'gateCredential',
  'eca': 'gateEca',
  'legal-status': 'gateStatus',
  'ohip-billing': 'gateOhip',
  'cpso-certificate': 'gateCpso',
  'ontario-practice': 'gateOntarioPractice',
};

const scoreColor = (total, max) => {
  const ratio = max > 0 ? total / max : 0;
  if (ratio >= 0.7) return '#16a34a';
  if (ratio >= 0.5) return '#d97706';
  return '#ea580c';
};

const OINPResults = ({
  result,
  onContinueToIntake,
  onDecline,
  onCheckEmployer,
  onShareEmployerChecklist,
  onSubscribeToAlerts,
  onResumeOriginalGoal,
  brand = null,
  darkMode = false,
  scheme = 'blue',
  language = 'en',
}) => {
  const tr = translator(language);
  const [alertEmail, setAlertEmail] = useState('');
  const [alertState, setAlertState] = useState('idle'); // idle | error | done
  const [printError, setPrintError] = useState('');
  const [consultNote, setConsultNote] = useState('');
  const [consultSent, setConsultSent] = useState(false);
  const expertName = brand?.expertName || '';

  if (!result) return null;

  const verdict = VERDICT_COPY[result.verdict] || VERDICT_COPY['needs-verification'];
  const hasScore = result.scoreState === 'complete' && result.total != null;
  // A failed gate means the score is unreachable on these answers, however high.
  const isBlocked = result.verdict === 'not-eligible';
  const failedGates = (result.gates || []).filter(g => g.status === 'fail');
  const isEmployerPath = result.path === 'employer';

  const handlePrint = () => {
    const opened = printScorecard({
      result,
      tr,
      deadlines: isEmployerPath ? DEADLINES : [],
      brand,
    });
    setPrintError(opened ? '' : tr('printBlocked'));
  };

  const handleSubscribe = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(alertEmail)) { setAlertState('error'); return; }
    onSubscribeToAlerts && onSubscribeToAlerts(alertEmail, result);
    setAlertState('done');
  };

  return (
    <Wrapper>
      <StepTitle $darkMode={darkMode}>{tr('resultsTitle')}</StepTitle>
      <StepSubtitle $darkMode={darkMode}>
        {tr('resultsSubtitle', { legalBasis: result.legalBasis, version: result.rulesVersion })}
      </StepSubtitle>

      {/* ---------------------------------------------------- eligibility -- */}
      <Card $darkMode={darkMode}>
        <CardHeading $darkMode={darkMode}>{tr('eligibilityHeading')}</CardHeading>
        <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 10, color: verdict.color }}>
          {tr(verdict.key)}
        </div>
        {result.gates.length > 0 && <GateList gates={result.gates} darkMode={darkMode} tr={tr} />}
      </Card>

      {/* ---------------------------------------------------------- score -- */}
      {/* A score and an eligibility verdict are independent: you can score well
          and still fail a gate. Leading with a big number when the verdict is
          "not eligible" reads as a pass — users see the score and conclude they
          qualify. So when a gate has failed, the number is muted, labelled as
          not currently achievable, and the failed requirement comes first. */}
      {hasScore && (
        <>
          <Eyebrow $darkMode={darkMode} $scheme={scheme} style={{ textAlign: 'center' }}>
            {tr(isBlocked ? 'scoreHeadingBlocked' : 'scoreHeading')}
          </Eyebrow>
          <Gauge
            value={result.total}
            max={result.maxScore || MAX_SCORE}
            color={isBlocked ? '#94a3b8' : scoreColor(result.total, result.maxScore || MAX_SCORE)}
            darkMode={darkMode}
            caption={tr('scoreOf', { max: result.maxScore || MAX_SCORE })}
          />
          {isBlocked && (
            <Note $tone="error" $darkMode={darkMode}>
              <strong>{tr('scoreBlockedTitle')}</strong>
              <br />
              {tr('scoreBlockedBody', { count: failedGates.length })}
              {failedGates.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {failedGates.map(gate => (
                    <li key={gate.id} style={{ marginBottom: 4 }}>
                      {tr(GATE_LABELS[gate.id] || gate.id)}
                      {gate.note && <><br /><span style={{ opacity: 0.8 }}>{tr(gate.note)}</span></>}
                    </li>
                  ))}
                </ul>
              )}
            </Note>
          )}
        </>
      )}

      {result.scoreState === 'unknown-noc' && (
        <Note $tone="error" $darkMode={darkMode}>
          <strong>{tr('scoreUnknownNocTitle')}</strong>
          <br />
          {tr('scoreUnknownNocBody')}
        </Note>
      )}

      {result.scoreState === 'incomplete-ruleset' && (
        <Note $tone="warn" $darkMode={darkMode}>
          <strong>{tr('scoreUnavailableTitle')}</strong>
          <br />
          {tr('scoreUnavailableBody', { count: result.missingFactors.length })}
        </Note>
      )}

      {result.path === 'physician' && (
        <Note $darkMode={darkMode}>{tr('physicianWageNote', { max: result.maxScore })}</Note>
      )}

      {/* ------------------------------------------------------ breakdown -- */}
      {result.breakdown.length > 0 && (
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('breakdownHeading')}</CardHeading>
          {result.breakdown.map(factor => {
            const isStrongest = result.strongestFactor && result.strongestFactor.id === factor.id;
            return (
              <Row key={factor.id} $darkMode={darkMode}>
                <span>
                  {tr(factor.labelKey)}
                  {isStrongest && (
                    <span style={{ marginLeft: 8, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.8, color: '#16a34a' }}>
                      {tr('strongest')}
                    </span>
                  )}
                </span>
                {factor.known ? (
                  <RowValue $darkMode={darkMode}>{factor.points} / {factor.max}</RowValue>
                ) : factor.notApplicable ? (
                  <RowValue $darkMode={darkMode} $muted>{tr('factorNotApplicable')}</RowValue>
                ) : (
                  <RowValue $darkMode={darkMode} $muted title={tr('factorPending')}>
                    — / {factor.max}
                  </RowValue>
                )}
              </Row>
            );
          })}
        </Card>
      )}

      {result.provenance === 'secondary' && (
        <Note $darkMode={darkMode}>{tr('provenanceSecondary')}</Note>
      )}

      {/* --------------------------------------------------------- boosts -- */}
      {result.boosts.length > 0 && (
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('boostHeading')}</CardHeading>
          <div style={{ fontSize: 11.5, marginBottom: 8, color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d' }}>
            {tr('boostSubtitle')}
          </div>
          {result.boosts.map(boost => (
            <Row key={boost.id} $darkMode={darkMode}>
              <span>{tr(boost.key, translateBoostParams(boost.params, tr))}</span>
              <RowValue $darkMode={darkMode} style={{ color: '#16a34a' }}>
                {tr('boostPoints', { gain: boost.gain })}
              </RowValue>
            </Row>
          ))}
        </Card>
      )}

      {isEmployerPath && <Note $darkMode={darkMode}>{tr('regionalInsight')}</Note>}

      {/* ------------------------------------------------------ deadlines -- */}
      {isEmployerPath && (
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('deadlinesHeading')}</CardHeading>
          {DEADLINES.map((deadline, index) => (
            <Row key={deadline.id} $darkMode={darkMode}>
              <span>
                <span style={{ opacity: 0.45, marginRight: 8, fontVariantNumeric: 'tabular-nums' }}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                {tr(deadline.textKey)}
              </span>
            </Row>
          ))}
        </Card>
      )}

      {/* ------------------------------------------- score card / alerts -- */}
      <Card $darkMode={darkMode}>
        <CardHeading $darkMode={darkMode}>{tr('alertsHeading')}</CardHeading>
        <div style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 12, color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
          {tr('alertsBody')}
        </div>
        {alertState === 'done' ? (
          <Note $darkMode={darkMode} style={{ marginBottom: 0 }}>{tr('alertsDone')}</Note>
        ) : (
          <>
            <InlineRow>
              <TextInput
                $darkMode={darkMode}
                $scheme={scheme}
                type="email"
                value={alertEmail}
                placeholder={tr('alertsPlaceholder')}
                onChange={e => { setAlertEmail(e.target.value); setAlertState('idle'); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSubscribe(); }}
              />
              <NextBtn type="button" $darkMode={darkMode} $scheme={scheme} onClick={handleSubscribe}>
                {tr('alertsSubscribe')}
              </NextBtn>
            </InlineRow>
            {alertState === 'error' && <ErrorText>{tr('alertsInvalidEmail')}</ErrorText>}
          </>
        )}
        <GhostBtn type="button" $darkMode={darkMode} onClick={handlePrint}>
          {tr('printScorecard')}
        </GhostBtn>
        {printError && <ErrorText>{printError}</ErrorText>}
      </Card>

      {/* -------------------------------------------------- employer CTA -- */}
      {isEmployerPath && (onCheckEmployer || onShareEmployerChecklist) && (
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('employerCtaHeading')}</CardHeading>
          <div style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 12, color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
            {tr('employerCtaBody')}
          </div>
          {onCheckEmployer && (
            <PrimaryBtn type="button" $darkMode={darkMode} $scheme={scheme} onClick={onCheckEmployer}>
              {tr('employerCtaCheck')}
            </PrimaryBtn>
          )}
          {onShareEmployerChecklist && (
            <GhostBtn type="button" $darkMode={darkMode} onClick={onShareEmployerChecklist}>
              {tr('employerCtaShare')}
            </GhostBtn>
          )}
        </Card>
      )}

      {/* -------------------------------------------------------- next up -- */}
      <div style={{ marginTop: 18 }}>
        {/* Continuing to consultation IS finishing the questionnaire. Everything
            answered above is handed over as a conversation and the case goes to
            the adviser — no need to start explaining it again in chat. */}
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('consultHeading')}</CardHeading>
          <div style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 12, color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
            {tr('consultBody', { expert: expertName || tr('consultFallbackExpert') })}
          </div>
          <TextInput
            as="textarea"
            rows={2}
            $darkMode={darkMode}
            $scheme={scheme}
            value={consultNote}
            placeholder={tr('consultNotePlaceholder')}
            onChange={e => setConsultNote(e.target.value)}
            style={{ resize: 'vertical', minHeight: 56, marginBottom: 10 }}
          />
          <PrimaryBtn
            type="button"
            $darkMode={darkMode}
            $scheme={scheme}
            disabled={consultSent}
            onClick={() => { setConsultSent(true); onContinueToIntake(consultNote); }}
          >
            {tr('continueCta')}
          </PrimaryBtn>
        </Card>

        {/* The Ontario check is often a detour. If they came in about something
            else, let them pick that back up instead of ending here. */}
        {onResumeOriginalGoal && !consultSent && (
          <GhostBtn type="button" $darkMode={darkMode} onClick={onResumeOriginalGoal}>
            {tr('resumeOriginalCta')}
          </GhostBtn>
        )}

        {onDecline && (
          <>
            <GhostBtn type="button" $darkMode={darkMode} onClick={onDecline}>
              {tr('declineCta')}
            </GhostBtn>
            <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              {tr('declineNote')}
            </div>
          </>
        )}
      </div>

      <Disclaimer $darkMode={darkMode}>{tr('disclaimer')}</Disclaimer>
    </Wrapper>
  );
};

// Boost params reference region ids; swap them for their translated labels.
function translateBoostParams(params, tr) {
  if (!params) return params;
  const REGION_KEYS = {
    'northern': 'regionNorthern',
    'eastern': 'regionEastern',
    'central-outside-gta': 'regionCentral',
    'southwestern': 'regionSouthwestern',
    'gta-except-toronto': 'regionGtaExTo',
    'toronto': 'regionToronto',
  };
  const out = { ...params };
  ['from', 'to'].forEach(field => {
    if (REGION_KEYS[out[field]]) out[field] = tr(REGION_KEYS[out[field]]);
  });
  return out;
}

export default OINPResults;
